// ─────────────────────────────────────────────────────────────────────────────
// New Item wizard — capa de datos (2026-10-14)
//
// Toda interacción con Sage pasa por aquí para que la pantalla
// (src/pages/Inventory/NewItemWizard) no sepa qué endpoint existe y cuál no.
//
//   ESCRITURA EN SAGE → /Test/EnvioOrden (ruta base, sin prefijo). El handler
//     reenvía req.body.data tal cual al worker de MAS90; el worker elige la
//     empresa por IdCompany y la acción por OrderType (.bat + csv).
//     Esa respuesta NO confirma nada (fire-and-forget), por eso todo se
//     verifica después con una lectura.
//   LECTURA / VERIFICACIÓN → {prefix}/sage/lookup (PROPUESTO). Debe leer Sage
//     en vivo por el WebSocket de la empresa, como /vendors/get/vendorsInformation.
//
// Los endpoints con `simulate: true` todavía no existen: se resuelven contra
// un "Sage falso" en localStorage para poder recorrer el wizard completo.
// Cuando backend los publique, poner `simulate: false` (o SIMULATE_ALL = false).
// La lista completa con payloads está en docs/new-item-wizard-endpoints.md.
// ─────────────────────────────────────────────────────────────────────────────
import { getInformationWithDataPrefix, getInformationWithDataV3 } from './CABE';
import { getValueCookie } from './cookieService';

export const COMPANIES = {
    3: { id: 3, name: 'Mayaland', prefix: '/my' },
    6: { id: 6, name: 'Altitude', prefix: '/alt' },
};
export const COMPANY_IDS = [3, 6];

// Interruptor general: true fuerza simulación en TODO lo que toca Sage,
// aunque el endpoint ya exista (útil para probar sin crear nada real).
export const SIMULATE_ALL = true;

export const WIZARD_ENDPOINTS = {
    // ── Lecturas que YA existen ─────────────────────────────────────────────
    vendorList:      { route: '/vendors/get/vendorsInformation', api: 'prefix', simulate: false }, // Sage en vivo por WS
    productLineList: { route: '/Items/get-exception-items',      api: 'prefix', simulate: false }, // RedStone IM_ProductLine (no distingue empresa)
    portalItemExists:{ route: '/v2/items/exists',                api: 'prefix', simulate: false }, // RedStone itemHeader
    pricingCompanies:{ route: '/v3/pricing/companies',           api: 'v3',     simulate: false },
    pricingUpsert:   { route: '/v3/pricing/upsert',              api: 'v3',     simulate: false }, // ADMIN_ONLY

    // ── Propuestos / TODO backend ───────────────────────────────────────────
    nextProductLine: { route: '/productline/get/nextProductLine', api: 'prefix', simulate: true, sage: true },
    nextVendorNo:    { route: '/vendors/get/nextVendorNo',        api: 'prefix', simulate: true, sage: true },
    sageLookup:      { route: '/sage/lookup',                     api: 'prefix', simulate: true, sage: true },
    sageSend:        { route: '/Test/EnvioOrden',                 api: 'base',   simulate: true, sage: true },
    itemCodeSearch:  { route: '/v3/items/search-codes',          api: 'v3',     simulate: true }, // mirror CI_Item por empresa, por prefijo
    portalItemCreate:{ route: '/v3/items/new-item',               api: 'v3',     simulate: true },
    portalItemImage: { route: '/v3/items/upload-image',           api: 'v3',     simulate: true },
};

export const ORDER_TYPES = {
    productLine: 'ImportProductLine',
    vendor: 'ImportVendor',
    item: 'CreateProducts',
    cost: 'UpdateItemCost', // NUEVO — ver documento de endpoints
};

const isSimulated = (key) => {
    const ep = WIZARD_ENDPOINTS[key];
    return !!(ep.simulate || (SIMULATE_ALL && ep.sage));
};
export const anySimulated = () => Object.keys(WIZARD_ENDPOINTS).some(isSimulated);

const ok = (res) => !!(res && res.status && (res.status.code === 1 || res.status === 'success'));
const msgOf = (res) => (res && (res.message || res.error)) || '';

const call = async (key, data, idCompany) => {
    const ep = WIZARD_ENDPOINTS[key];
    if (ep.api === 'v3') return getInformationWithDataV3(ep.route, data);
    const prefix = ep.api === 'prefix' && COMPANIES[idCompany] ? COMPANIES[idCompany].prefix : '';
    return getInformationWithDataPrefix(ep.route, data, prefix);
};

// ─── Sage simulado ────────────────────────────────────────────────────────────
// Guarda lo "creado" por empresa, para que lookup lo encuentre tras un retraso
// (como el .bat real). El modo de fallo se elige desde el banner de la pantalla.
const MOCK_KEY = 'newItemWizard.mockSage.v1';
const MOCK_MODE_KEY = 'newItemWizard.mockMode';
export const MOCK_MODES = {
    ok: 'Everything works',
    unconfirmed: "Sage doesn't confirm",
    reject: 'Request rejected',
};
const MOCK_APPLY_DELAY_MS = 2500;

const readJson = (key, fallback) => {
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
        return fallback;
    }
};
const writeJson = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage lleno o bloqueado */ }
};

export const getMockMode = () => {
    try { return window.localStorage.getItem(MOCK_MODE_KEY) || 'ok'; } catch (e) { return 'ok'; }
};
export const setMockMode = (mode) => {
    try { window.localStorage.setItem(MOCK_MODE_KEY, mode); } catch (e) { /* ignore */ }
};

const mockDb = () => readJson(MOCK_KEY, {});
const mockTable = (db, idCompany, entity) => {
    if (!db[idCompany]) db[idCompany] = {};
    if (!db[idCompany][entity]) db[idCompany][entity] = {};
    return db[idCompany][entity];
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const MOCK_ENTITY_BY_ORDER = {
    ImportProductLine: { entity: 'productLine', key: 'ProductLine' },
    ImportVendor: { entity: 'vendor', key: 'VendorNo' },
    CreateProducts: { entity: 'item', key: 'ItemCode' },
    UpdateItemCost: { entity: 'item', key: 'ItemCode', merge: true },
};

const mockSend = async (data) => {
    await sleep(600);
    const mode = getMockMode();
    if (mode === 'reject') return { status: { code: 0 }, httpStatus: 400, message: 'Simulated rejection' };
    if (mode === 'unconfirmed') return { status: { code: 1 } }; // "ok" pero nunca aparece en Sage
    const meta = MOCK_ENTITY_BY_ORDER[data.OrderType];
    if (!meta) return { status: { code: 0 }, httpStatus: 400, message: 'Unknown OrderType' };
    const rows = data.OrderType === 'CreateProducts' ? data.Products : [data];
    setTimeout(() => {
        const db = mockDb();
        const table = mockTable(db, data.IdCompany, meta.entity);
        rows.forEach((row) => {
            const k = String(row[meta.key]).toUpperCase();
            table[k] = meta.merge ? { ...(table[k] || {}), ...row } : row;
        });
        writeJson(MOCK_KEY, db);
    }, MOCK_APPLY_DELAY_MS);
    return { status: { code: 1 } };
};

// Items de ejemplo del Sage simulado, para poder ver la sucesión de códigos
// en el autocompletado sin backend (PK1001…PK1006, FIX1001…FIX1002, etc.).
const MOCK_SAMPLE_ITEMS = [
    ['PK1001', 'POTATO CHIPS SALT 12OZ', 'PK01'], ['PK1002', 'POTATO CHIPS BBQ 12OZ', 'PK01'], ['PK1003', 'POTATO CHIPS LIME 12OZ', 'PK01'],
    ['PK1004', 'TORTILLA CHIPS 16OZ', 'PK01'], ['PK1005', 'CORN CHIPS 10OZ', 'PK01'], ['PK1006', 'PORK RINDS 4OZ', 'PK02', 'Y'],
    ['PK2001', 'POPCORN BUTTER 6CT', 'PK02'], ['PK2002', 'POPCORN KETTLE 6CT', 'PK02'],
    ['FIX1001', 'FIX BRACKET 1IN', 'FX01'], ['FIX1002', 'FIX BRACKET 2IN', 'FX01'],
    ['FOOD-001', 'RICE WHITE 5LB', 'FD01'], ['FOOD-002', 'RICE BROWN 5LB', 'FD01'], ['FOOD-010', 'BEANS PINTO 4LB', 'FD01'],
].map(([ItemCode, Description, ProductLineNo, inactive]) => ({ ItemCode, Description, ProductLineNo, VendorNo: '0000001', InactiveItem: inactive || 'N' }));

const mockItems = (idCompany) => {
    const created = mockTable(mockDb(), idCompany, 'item');
    const all = {};
    MOCK_SAMPLE_ITEMS.forEach((r) => { all[r.ItemCode] = r; });
    Object.keys(created).forEach((k) => { all[k] = { ...(all[k] || {}), ...created[k] }; });
    return all;
};

const mockLookup = async (entity, idCompany, key) => {
    await sleep(400);
    const table = entity === 'item' ? mockItems(idCompany) : mockTable(mockDb(), idCompany, entity);
    const record = table[String(key).toUpperCase()];
    return { ok: true, exists: !!record, record: record || null };
};

const mockNextNo = (list, width) => {
    const max = list.reduce((m, code) => {
        const n = parseInt(String(code).replace(/\D/g, ''), 10);
        return isFinite(n) && n > m ? n : m;
    }, 0);
    return String(max + 1).padStart(width, '0');
};

// ─── Normalizadores ───────────────────────────────────────────────────────────
const pick = (obj, keys) => {
    for (let i = 0; i < keys.length; i++) {
        const v = obj[keys[i]];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
};
const rowsOf = (res) => {
    if (!res) return [];
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.data)) return res.data.data;
    return [];
};

// ─── Listas ───────────────────────────────────────────────────────────────────
export const listProductLines = async (idCompany) => {
    const res = await call('productLineList', {}, idCompany);
    if (!ok(res) && !Array.isArray(res && res.data)) return { ok: false, rows: [], message: msgOf(res) || 'Could not load product lines.' };
    const rows = rowsOf(res).map((p) => ({
        code: String(pick(p, ['ProductLine', 'productLine'])).trim(),
        desc: String(pick(p, ['ProductLineDesc', 'productLineDesc'])).trim(),
    })).filter((p) => p.code);
    // Lo creado en el Sage simulado también aparece, para poder re-elegirlo.
    if (isSimulated('sageSend')) {
        const mock = mockTable(mockDb(), idCompany, 'productLine');
        Object.keys(mock).forEach((k) => {
            if (!rows.some((r) => r.code.toUpperCase() === k)) rows.push({ code: mock[k].ProductLine, desc: mock[k].ProductLineDesc });
        });
    }
    return { ok: true, rows };
};

export const listVendors = async (idCompany) => {
    const res = await call('vendorList', { idCompany }, idCompany);
    if (!ok(res) && !Array.isArray(res && res.data)) return { ok: false, rows: [], message: msgOf(res) || 'Could not load vendors from Sage.' };
    const rows = rowsOf(res).map((v) => ({
        code: String(pick(v, ['VendorNo', 'vendorNo', 'customerNo'])).trim(),
        name: String(pick(v, ['VendorName', 'name'])).trim(),
        city: String(pick(v, ['City', 'city'])).trim(),
    })).filter((v) => v.code);
    if (isSimulated('sageSend')) {
        const mock = mockTable(mockDb(), idCompany, 'vendor');
        Object.keys(mock).forEach((k) => {
            if (!rows.some((r) => r.code.toUpperCase() === k)) rows.push({ code: mock[k].VendorNo, name: mock[k].VendorName, city: mock[k].VendorCity });
        });
    }
    return { ok: true, rows };
};

// Autocompletado del ItemCode: items de la empresa (cualquier product line)
// cuyo código empieza con `prefix`. Lee el mirror CI_Item, así que un item
// recién creado puede tardar en aparecer; el código exacto siempre se
// confirma aparte con lookupInSage.
export const ITEM_SEARCH_LIMIT = 50;
export const searchItemCodes = async (idCompany, prefix) => {
    const p = String(prefix || '').trim().toUpperCase();
    if (isSimulated('itemCodeSearch')) {
        await sleep(300);
        const all = mockItems(idCompany);
        const matches = Object.keys(all).filter((k) => k.indexOf(p) === 0).map((k) => all[k]);
        const rows = matches.map((r) => ({
            itemCode: r.ItemCode, description: r.Description || '', productLine: r.ProductLineNo || '', inactive: r.InactiveItem === 'Y',
        }));
        return { ok: true, rows: rows.slice(0, ITEM_SEARCH_LIMIT), total: rows.length };
    }
    const res = await call('itemCodeSearch', { idCompany, prefix: p, limit: ITEM_SEARCH_LIMIT });
    if (!ok(res)) return { ok: false, rows: [], total: 0, message: msgOf(res) || 'Could not search item codes.' };
    const rows = rowsOf(res).map((r) => ({
        itemCode: String(pick(r, ['ItemCode', 'itemCode'])).trim(),
        description: String(pick(r, ['ItemCodeDesc', 'description'])).trim(),
        productLine: String(pick(r, ['ProductLine', 'productLine'])).trim(),
        inactive: String(pick(r, ['InactiveItem', 'inactive'])).toUpperCase() === 'Y',
    })).filter((r) => r.itemCode);
    return { ok: true, rows, total: Number(res.total) || rows.length };
};

// ─── Correlativos ────────────────────────────────────────────────────────────
const extractNextNo = (res) => {
    if (!ok(res)) return '';
    const d = res.data;
    if (d && d.nextNo !== undefined && d.nextNo !== null) return String(d.nextNo);
    return d !== undefined && d !== null && typeof d !== 'object' ? String(d) : '';
};

export const getNextProductLine = async (idCompany, existingCodes) => {
    if (isSimulated('nextProductLine')) return { ok: true, value: mockNextNo(existingCodes || [], 4) };
    const res = await call('nextProductLine', { IdCompany: idCompany }, idCompany);
    const value = extractNextNo(res);
    return value ? { ok: true, value } : { ok: false, message: msgOf(res) || 'Could not get the next Product Line number.' };
};

export const getNextVendorNo = async (idCompany, existingCodes) => {
    if (isSimulated('nextVendorNo')) return { ok: true, value: mockNextNo(existingCodes || [], 7) };
    const res = await call('nextVendorNo', { IdCompany: idCompany }, idCompany);
    const value = extractNextNo(res);
    return value ? { ok: true, value } : { ok: false, message: msgOf(res) || 'Could not get the next Vendor number.' };
};

// ─── Sage: enviar + verificar ────────────────────────────────────────────────
// Respuesta de envío: { definite: true } solo cuando es un rechazo claro (4xx)
// y por lo tanto NADA llegó a Sage. Timeout / red / 5xx / "ok" son ambiguos:
// hay que verificar antes de pensar en reenviar.
export const sendToSage = async (data) => {
    const payload = { ...data, updateBy: getValueCookie('userName') };
    const res = isSimulated('sageSend') ? await mockSend(payload) : await call('sageSend', payload);
    const rejected = !ok(res) && res && res.httpStatus >= 400 && res.httpStatus < 500;
    return { rejected: !!rejected, message: msgOf(res) };
};

// entity: 'productLine' | 'vendor' | 'item'. Devuelve { ok, exists, record }.
// ok=false → no se pudo preguntar (red/timeout), NO significa "no existe".
export const lookupInSage = async (entity, idCompany, key) => {
    if (isSimulated('sageLookup')) return mockLookup(entity, idCompany, key);
    const res = await call('sageLookup', { entity, IdCompany: idCompany, key }, idCompany);
    if (!ok(res)) return { ok: false, exists: false, record: null, message: msgOf(res) || 'No response from Sage.' };
    const d = res.data || {};
    return { ok: true, exists: !!d.exists, record: d.record || null };
};

// ─── Portal ──────────────────────────────────────────────────────────────────
export const checkPortalDuplicates = async (idCompany, itemCode, upc) => {
    const res = await call('portalItemExists', { ItemCode: itemCode, Upc: upc }, idCompany);
    if (!ok(res)) return { ok: false, message: msgOf(res) || 'Could not check duplicates in the portal.' };
    const row = Array.isArray(res.data) ? (res.data[0] || {}) : (res.data || {});
    return { ok: true, itemCodeExists: Number(row.item_code_exists) > 0, upcExists: Number(row.upc_exists) > 0 };
};

export const createPortalItem = async (payload) => {
    if (isSimulated('portalItemCreate')) { await sleep(500); return { ok: true }; }
    const res = await call('portalItemCreate', payload);
    return ok(res) ? { ok: true } : { ok: false, message: msgOf(res) || 'Could not save the item in the portal.' };
};

export const uploadItemImage = async (itemCode, fileName, dataUrl) => {
    if (isSimulated('portalItemImage')) { await sleep(500); return { ok: true, url: '' }; }
    const res = await call('portalItemImage', { itemCode, fileName, image: dataUrl });
    return ok(res) ? { ok: true, url: (res.data && res.data.url) || '' } : { ok: false, message: msgOf(res) || 'Could not upload the image.' };
};

export const getPricingCompanies = async () => {
    const res = await call('pricingCompanies', {});
    if (!ok(res)) return [];
    return rowsOf(res).map((c) => ({ idCompany: Number(c.idCompany), parentCompanyId: c.parentCompanyId ? Number(c.parentCompanyId) : null }));
};

// Niveles al portal, igual que Price Management. El costo NO va aquí (solo a
// Sage, decisión 2026-10-14); upsert cae al costo sincronizado del catálogo.
export const savePortalPricing = async (itemCode, idCompany, levels) => {
    const res = await call('pricingUpsert', {
        itemCode,
        idCompany,
        standardPrice: levels.level1Price,
        level1Price: levels.level1Price,
        level2Price: levels.level2Price,
        level3Price: levels.level3Price,
        level4Price: levels.level4Price,
        allowLevel1BelowCost: false,
        allowLevel2BelowCost: false,
        allowLevel3BelowCost: false,
        allowLevel4BelowCost: false,
    });
    return ok(res) ? { ok: true } : { ok: false, message: msgOf(res) || 'Could not save the prices in the portal.' };
};
